import React, { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { Link, useLocation } from 'react-router-dom';

// 定義路由配置和名稱映射
interface RouteConfig {
  path: string;
  breadcrumbName: string;
  children?: RouteConfig[];
  isParam?: boolean;  // 標記該部分是否為參數 (如 :id)
}

// 參數型頁面配置
interface ParamPageConfig {
  pattern: RegExp;  // 用於匹配的正則表達式
  breadcrumbs: string[];  // 麵包屑路徑數組（從根路徑開始）
  title: string;  // 參數頁面的標題
}

// 系統選單路由配置
const routes: RouteConfig[] = [
  {
    path: '/',
    breadcrumbName: '首頁',
    children: [
      {
        path: '/membership-management',
        breadcrumbName: '會員檔案維護與查詢',
        children: [
          {
            path: '/membership-master',
            breadcrumbName: '會籍主檔',
            children: []
          },
          {
            path: '/product-master',
            breadcrumbName: '實際產品主檔',
            children: []
          },
          {
            path: '/certificate-master',
            breadcrumbName: '認證主檔',
            children: []
          },
        ],
      },
      {
        path: '/other',
        breadcrumbName: '其他',
        children: [
          {
            path: '/price-certificate',
            breadcrumbName: '比價證明書列印',
          },
          {
            path: '/member-grade-check',
            breadcrumbName: '會員級數錯誤檢驗',
          },
          {
            path: '/member-statistics',
            breadcrumbName: '會員組織統計',
          },
          {
            path: '/member-directory-export',
            breadcrumbName: '會員名錄匯出',
          },
          {
            path: '/zongjiu-export',
            breadcrumbName: '會員資料轉宗九匯出',
          },
        ],
      },
      {
        path: '/professional-committee',
        breadcrumbName: '專業委員會',
        children: [
          {
            path: '/committee',
            breadcrumbName: '專委會',
          },
        ],
      },
      {
        path: '/activity-management',
        breadcrumbName: '活動維護',
        children: [
          {
            path: '/association-activity',
            breadcrumbName: '本會活動維護',
          },
        ],
      },
    ],
  },
];

// 參數頁面配置
const paramPages: ParamPageConfig[] = [
  // 會員詳情頁面
  {
    pattern: /^\/membership\/detail\/(test|\d+)$/,
    breadcrumbs: ['/', '/membership-management', '/membership-master'],
    title: '會員主檔詳細資料'
  },
  // 認證詳情頁面
  {
    pattern: /^\/certificate\/detail\/\d+$/,
    breadcrumbs: ['/', '/membership-management', '/certificate-master'],
    title: '認證詳細資料'
  },
  // 產品詳情頁面
  {
    pattern: /^\/product\/detail\/\d+$/,
    breadcrumbs: ['/', '/membership-management', '/product-master'],
    title: '產品詳細資料'
  },
];

// 直接路徑映射表 - 用於處理特殊情況和不規則路徑
const pathMappings: Record<string, string[]> = {
  // 特殊路徑映射到對應的麵包屑路徑 (格式: '實際路徑': ['麵包屑層級路徑1', '麵包屑層級路徑2', ...])
  '/membership-master': ['/', '/membership-management', '/membership-master'],
  '/product-master': ['/', '/membership-management', '/product-master'],
  '/certificate-master': ['/', '/membership-management', '/certificate-master'],
  '/price-certificate': ['/', '/other', '/price-certificate'],
  '/member-grade-check': ['/', '/other', '/member-grade-check'],
  '/member-statistics': ['/', '/other', '/member-statistics'],
  '/member-directory-export': ['/', '/other', '/member-directory-export'],
  '/zongjiu-export': ['/', '/other', '/zongjiu-export'],
  '/committee': ['/', '/professional-committee', '/committee'],
  '/association-activity': ['/', '/activity-management', '/association-activity'],
};

// 平面化路由配置為路徑映射，方便查詢
const getPathMap = () => {
  const pathMap = new Map<string, RouteConfig>();
  
  const flattenRoutes = (routeConfig: RouteConfig) => {
    pathMap.set(routeConfig.path, routeConfig);
    
    if (routeConfig.children && routeConfig.children.length > 0) {
      routeConfig.children.forEach(child => flattenRoutes(child));
    }
  };
  
  routes.forEach(route => flattenRoutes(route));
  return pathMap;
};

// 根據當前路徑生成麵包屑項目
const generateBreadcrumbs = (pathname: string): RouteConfig[] => {
  const pathMap = getPathMap();
  
  // 首先清除路徑中的尾部斜線和重複斜線
  const normalizedPath = pathname.replace(/\/+$/, '').replace(/\/+/g, '/');
  
  // 檢查是否為參數頁面
  for (const paramConfig of paramPages) {
    if (paramConfig.pattern.test(normalizedPath)) {
      // 生成參數頁面的麵包屑
      const breadcrumbs: RouteConfig[] = [];
      
      // 添加所有前置路徑
      for (const path of paramConfig.breadcrumbs) {
        if (pathMap.has(path)) {
          breadcrumbs.push(pathMap.get(path)!);
        }
      }
      
      // 添加當前參數頁面
      breadcrumbs.push({
        path: normalizedPath,
        breadcrumbName: paramConfig.title,
        isParam: true
      });
      
      return breadcrumbs;
    }
  }
  
  // 檢查是否有直接路徑映射
  if (normalizedPath in pathMappings) {
    const breadcrumbs: RouteConfig[] = [];
    
    // 添加映射的路徑
    for (const path of pathMappings[normalizedPath]) {
      if (pathMap.has(path)) {
        breadcrumbs.push(pathMap.get(path)!);
      }
    }
    
    return breadcrumbs;
  }
  
  // 常規路徑處理 - 分段方式
  const pathParts = normalizedPath.split('/').filter(Boolean);
  let currentPath = '/';
  const breadcrumbs: RouteConfig[] = [];
  
  // 添加根路徑
  if (pathMap.has(currentPath)) {
    breadcrumbs.push(pathMap.get(currentPath)!);
  }
  
  // 想處理特殊情況，如 /xxx-master 屬於 /membership-management
  if (normalizedPath.endsWith('-master')) {
    // 如果是主檔頁面，則嘗試添加 membership-management 父路徑
    if (pathMap.has('/membership-management')) {
      breadcrumbs.push(pathMap.get('/membership-management')!);
    }
  }
  
  // 逐段構建路徑並查找對應配置
  for (const part of pathParts) {
    const partPath = `/${part}`;
    
    // 直接檢查第一層路徑
    if (pathMap.has(partPath)) {
      breadcrumbs.push(pathMap.get(partPath)!);
      continue;
    }
    
    // 否則，結合前置路徑
    currentPath += part;
    if (!currentPath.startsWith('/')) {
      currentPath = `/${currentPath}`;
    }
    
    if (pathMap.has(currentPath)) {
      breadcrumbs.push(pathMap.get(currentPath)!);
    } else {
      currentPath += '/';
    }
  }
  
  return breadcrumbs;
};

// BreadcrumbNav 組件
const BreadcrumbNav: React.FC = () => {
  const location = useLocation();
  
  // 使用 useMemo 避免不必要的重複計算
  const breadcrumbs = useMemo(() => {
    // 調試日誌
    console.log('Generating breadcrumbs for:', location.pathname);
    return generateBreadcrumbs(location.pathname);
  }, [location.pathname]);
  
  // 如果沒有找到麵包屑配置，則使用預設樣式
  if (!breadcrumbs || breadcrumbs.length === 0) {
    console.warn('No breadcrumbs found for:', location.pathname);
    
    // 嘗試根據路徑結構自動生成一個基本的麵包屑
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      return (
        <Breadcrumb className="breadcrumb-nav">
          <Breadcrumb.Item>
            <Link to="/">首頁</Link>
          </Breadcrumb.Item>
          {pathParts.map((part, index) => {
            const isLast = index === pathParts.length - 1;
            const partPath = `/${part}`;
            
            // 最後一個項目不可點擊
            if (isLast) {
              return <Breadcrumb.Item key={partPath}>{part.replace(/-/g, ' ')}</Breadcrumb.Item>;
            }
            
            // 其他項目可點擊
            const fullPath = `/${pathParts.slice(0, index + 1).join('/')}`;
            return (
              <Breadcrumb.Item key={fullPath}>
                <Link to={fullPath}>{part.replace(/-/g, ' ')}</Link>
              </Breadcrumb.Item>
            );
          })}
        </Breadcrumb>
      );
    }
    
    // 最简化的预设面包屑
    return (
      <Breadcrumb className="breadcrumb-nav">
        <Breadcrumb.Item>
          <Link to="/">首頁</Link>
        </Breadcrumb.Item>
      </Breadcrumb>
    );
  }
  
  return (
    <Breadcrumb className="breadcrumb-nav">
      {breadcrumbs.map((route, index) => {
        const isLast = index === breadcrumbs.length - 1;
        
        return (
          <Breadcrumb.Item key={route.path}>
            {!isLast ? (
              <Link to={route.path}>{route.breadcrumbName}</Link>
            ) : (
              route.breadcrumbName
            )}
          </Breadcrumb.Item>
        );
      })}
    </Breadcrumb>
  );
};

export default BreadcrumbNav;
